import { Intercom, update } from '@intercom/messenger-js-sdk';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useWxCopilotLayout } from 'components/GlobalNav/context';
import { makeSelectWhoami } from 'containers/Auth_old/selectors';

const CUE_PANEL_WIDTH = 400;

function getQueryParam(search: string, paramName: string) {
  const urlParams = new URLSearchParams(search);
  return urlParams.get(paramName);
}

export const IntercomMessenger = ({
  copilotEnabled = false,
}: {
  copilotEnabled?: boolean;
}) => {
  const location = useLocation();
  const { isPanelOpen, isWxCopilotReady } = useWxCopilotLayout();

  const persistedPopupType = useMemo(
    () => getQueryParam(location.search, 'persistedPopupType'),
    [location.search],
  );

  /* eslint-disable camelcase */
  const {
    account_name_v2: company,
    account_name: slug,
    name,
    email,
    role,
    intercom_digest: user_hash,
    created_at: createdAtISO,
  } = useSelector(makeSelectWhoami());

  const created_at = createdAtISO ? Date.parse(createdAtISO) : null;
  const isIntercomInitialized = useRef(false);

  // Claim Intercom ownership at mount — before whoami/email resolves and
  // before the Cue UMD readiness wait. The wx-navbar UMD checks this flag
  // (in addition to window.Intercom) so its HMAC call resolving during
  // either wait can't boot a second, conflicting Intercom identity. If
  // Hire is mounted, Hire owns Intercom.
  useEffect(() => {
    (
      window as Window & { __fountainHostOwnsIntercom?: boolean }
    ).__fountainHostOwnsIntercom = true;
  }, []);

  useEffect(() => {
    if (!email) return;
    // Wait for the Cue UMD readiness signal so Intercom boots with the
    // correct panel-aware padding (a 2s fallback in the provider guarantees
    // this never blocks Intercom indefinitely).
    if (copilotEnabled && !isWxCopilotReady) return;

    const copilotOptions = copilotEnabled
      ? {
          vertical_padding: 80,
          horizontal_padding: isPanelOpen ? CUE_PANEL_WIDTH + 20 : 0,
        }
      : {};

    if (!isIntercomInitialized.current) {
      Intercom({
        app_id: 'h9dh2723',
        user_hash,
        name,
        email,
        ...(created_at ? { created_at } : {}),
        company,
        Role: role,
        slug,
        hide_default_launcher: persistedPopupType === 'applicantDrawerPopup',
        ...copilotOptions,
      });
      isIntercomInitialized.current = true;
    } else {
      update({
        hide_default_launcher: persistedPopupType === 'applicantDrawerPopup',
        ...copilotOptions,
      });
    }
  }, [
    persistedPopupType,
    user_hash,
    name,
    email,
    company,
    role,
    slug,
    copilotEnabled,
    isPanelOpen,
    isWxCopilotReady,
  ]);
  /* eslint-enable camelcase */
  return null;
};
